/**
 * Google Places API Service (v1 - New)
 * Returns raw Google Places API responses for components to consume
 */

export class GooglePlacesApiService {
  private static userLocation: { latitude: number; longitude: number } | null = null

  private static getApiKey(): string {
    const apiKey = process.env.VUE_APP_GOOGLE_MAPS_KEY
    if (!apiKey) {
      throw new Error('Location search is not configured. Please contact support.')
    }
    return apiKey
  }

  private static async getUserLocation(): Promise<{
    latitude: number
    longitude: number
  } | null> {
    if (this.userLocation) {
      return this.userLocation
    }

    if (!navigator.geolocation) {
      return null
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.userLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
          resolve(this.userLocation)
        },
        () => {
          resolve(null)
        }
      )
    })
  }

  /**
   * Get autocomplete predictions for a search query
   * Returns raw API response
   */
  static async getPredictions(input: string): Promise<any> {
    if (!input || input.trim().length === 0) {
      return { suggestions: [] }
    }

    const apiKey = this.getApiKey()

    const userLocation = await this.getUserLocation()

    const requestBody: any = {
      textQuery: input
    }

    if (userLocation) {
      requestBody.locationBias = {
        circle: {
          center: {
            latitude: userLocation.latitude,
            longitude: userLocation.longitude
          },
          radius: 10000.0
        }
      }
    }

    const response = await fetch(
      'https://places.googleapis.com/v1/places:searchText',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'places.id,places.displayName,places.formattedAddress,places.location,places.name'
        },
        body: JSON.stringify(requestBody)
      }
    )

    return response.json()
  }

  /**
   * Get detailed information about a place by place_id
   * Returns raw API response
   */
  static async getPlaceDetails(placeId: string): Promise<any> {
    const apiKey = this.getApiKey()
    const placeName = placeId.startsWith('places/') ? placeId : `places/${placeId}`

    const response = await fetch(
      `https://places.googleapis.com/v1/${placeName}`,
      {
        method: 'GET',
        headers: {
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'id,displayName,formattedAddress,location,name'
        }
      }
    )

    return response.json()
  }
}
